import { CSSProperties } from 'react';
import cls from 'classnames';

import Link, { NONE } from '@/common/components/Link/Link';

import { MEDIA_SIZE_LARGE, MediaSize } from '../../constants/sizeCodes';
import { MEDIA_SIZE_MAP } from '../../constants/sizeCodeToPx';
import Media from '../../entity/Media';
import MediaSource from '../../entity/MediaSource';
import { MediaSubFile } from '../../entity/MediaSubFile';
import FSetImage from '../FSetImage/FSetImage';

import styles from './Image.module.css';

interface ImageProps {
  media?: Nullable<Media>;
  mediaSource?: Nullable<MediaSource>;
  mediaSubFile?: Nullable<Pick<MediaSubFile, 'externalLink'>>;
  className?: string;
  mediaSize?: Nullable<MediaSize | string>;
  containerSize?: Nullable<MediaSize | string>;
  maxScale?: Nullable<number>;
  isSingleScale?: Nullable<boolean>;
  isGrayscale?: Nullable<boolean>;
  isSaturation?: Nullable<boolean>;
  href?: string;
  hqMode?: boolean;
  previewFrame?: Nullable<number>;
}

function Image(props: ImageProps) {
  const {
    media = null,
    mediaSource = null,
    mediaSubFile = null,
    className = '',
    mediaSize = MEDIA_SIZE_LARGE,
    containerSize = null,
    maxScale = null,
    isSingleScale = false,
    isGrayscale = false,
    isSaturation = false,
    href = '',
    hqMode = false,
    previewFrame = null,
  } = props;

  const containerSizeInPx = MEDIA_SIZE_MAP[(containerSize || mediaSize) as MediaSize];

  function renderPolishImage() {
    return <img className={styles.mediaSectionFit} src={mediaSubFile ? mediaSubFile.externalLink : ''} alt="Polish" />;
  }

  function renderImage() {
    const subFileMediaSize = MEDIA_SIZE_MAP[mediaSize as MediaSize] * (hqMode ? 2 : 1);
    const subFile = mediaSource!.getMaxSubFileByMediaSize(subFileMediaSize);

    if (!subFile) {
      return null;
    }

    let width = subFile.resolutionX;
    let height = subFile.resolutionY;

    const containerScaleRatio = containerSizeInPx / Math.max(width, height);

    let singleScaleRatio = 1;

    if (isSingleScale) {
      if (mediaSource!.scaleRatio && maxScale) {
        singleScaleRatio = mediaSource!.relScale / maxScale;
      }
      const maxSide = Math.max(height, width);

      if (maxSide < subFileMediaSize) {
        singleScaleRatio *= subFileMediaSize / maxSide;
      }
    }

    width *= singleScaleRatio * containerScaleRatio;
    height *= singleScaleRatio * containerScaleRatio;
    const imageStyles = isSingleScale ? { width: `${width}px`, height: `${height}px` } : {};

    let image;

    if (media!.isVideo) {
      image = <FSetImage style={imageStyles} mediaSubFile={subFile} previewFrame={previewFrame} />;
    } else {
      image = (
        <img
          draggable="false"
          style={imageStyles}
          src={subFile ? subFile.externalLink : ''}
          alt={media!.setupPreset.title}
        />
      );
    }

    return href ? (
      <Link href={href} appearance={NONE} aria-label="Open media">
        {image}
      </Link>
    ) : (
      image
    );
  }

  if (mediaSubFile) {
    return (
      <div
        className={cls(styles.mediaSection, className)}
        style={{ '--media-section-size': `${containerSizeInPx}px` } as CSSProperties}
      >
        {renderPolishImage()}
      </div>
    );
  }

  return (
    <div
      className={cls(
        styles.mediaSection,
        className,
        isGrayscale ? styles.mediaSectionGrayscale : '',
        isSaturation ? styles.mediaSectionSaturation : '',
      )}
      style={
        {
          backgroundColor: mediaSource ? mediaSource.bgColorOverrided : media!.bgColor,
          '--media-section-size': `${containerSizeInPx}px`,
        } as CSSProperties
      }
    >
      {renderImage()}
    </div>
  );
}

export default Image;
